<template>
  <h2 class="text-bold page-title">
    {{ getMessageByLang("categoriesText", "Категории") }}
  </h2>

  <VLoader v-show="loader" />
  <div v-show="!loader">
    <TreeList
      :style="{
        'max-height': '510px',
        overflow: 'auto',
      }"
      :navigatable="true"
      :expand-field="expandField"
      :sub-items-field="subItemsField"
      @expandchange="onExpandChange"
      :data-items="processedData"
      :columns="columns"
      :toolbar="'toolbar'"
    >
      <template v-slot:toolbar>
        <div>
          <Button title="Add new" :theme-color="'primary'" @click="create">
            {{ getMessageByLang("addText", "Добавить") }}
          </Button>
        </div>
      </template>
      <template v-slot:operation="{ props }">
        <td class="k-command-cell k-table-td">
          <div class="cell-control" v-if="getRole() === 'admin'">
            <Button
              title="Edit"
              :theme-color="'primary'"
              :svg-icon="pencilIcon"
              @click="edit(props.dataItem)"
            >
            </Button>
            <Button :svg-icon="trashIcon" @click="showModalDelete(props.dataItem)" />
          </div>
        </td>
      </template>
    </TreeList>
  </div>

  <Dialog
    v-if="itemInCreate"
    :data-item="itemInCreate"
    :width="600"
    :height="600"
    @save="createCategory"
    @cancel="cancel"
  >
    <h3 class="custom-modal-title">
      {{ getMessageByLang("createText", "Создание") }}
    </h3>

    <Form
      ref="categoryFormCreate"
      @submit="handleCreateSubmit"
      :initial-values="itemInCreate"
      :ignoreModified="true"
    >
      <CategoryForm ref="RefCategoryForm" />

      <div class="category-photo-block">
        <div class="category-photo-block__upload" v-if="getRole() === 'admin'">
          <label>{{ getMessageByLang("addFileTitle", "addFileTitle") }}</label>

          <div>
            <label for="AddCategoryUpload" class="app-upload-label">Выберите файл</label>
            <Upload @add="onAddFile" @remove="onRemoveFile" id="AddCategoryUpload" class="app-upload" />
          </div>
        </div>

        <ul class="category-photo-block__list">
          <li v-for="(photo, indexPhoto) in photosList" :key="photo">
            <CardPhoto
              :img="photo.img"
              :file="photo"
              :isGenerate="true"
              @remove="handleRemove"
              :index="indexPhoto"
              is-hide-remove-btn
            />
            <Button
              class="category-photo-block__remove"
              :svg-icon="trashIcon"
              @click="handleRemove"
              v-if="getRole() === 'admin'"
            />
          </li>
        </ul>
      </div>
    </Form>

    <div class="custom-modal-footer">
      <Button @click="cancel">
        {{ getMessageByLang("cancel", "Отмена") }}
      </Button>
      <Button :theme-color="'primary'" @click="createCategory">
        {{ getMessageByLang("btnSave", "Сохранить") }}
      </Button>
    </div>
  </Dialog>

  <Dialog
    v-if="itemInEdit"
    :data-item="itemInEdit"
    :width="600"
    :height="600"
    @save="updateCategory"
    @cancel="cancel"
  >
    <h3 class="custom-modal-title">
      {{ getMessageByLang("editText", "Редактирование") }}
    </h3>

    <VLoader v-if="isLoadingEdit" />
    <div v-else>
      <Form
        ref="categoryFormUpdate"
        @submit="handleUpdateSubmit"
        :initial-values="itemInEdit"
        :ignoreModified="true"
      >
        <CategoryForm :defaulValue="itemInEdit" ref="RefCategoryForm" />

        <div class="category-photo-block">
          <div class="category-photo-block__upload">
            <label>{{
              getMessageByLang("addFileTitle", "addFileTitle")
            }}</label>

            <div>
              <label for="EditCategoryUpload" class="app-upload-label">Выберите файл</label>
              <Upload
                  @add="onAddFile"
                  @remove="onRemoveFile"
                  id="EditCategoryUpload"
                  class="app-upload"
                  v-if="getRole() === 'admin'"
              />
            </div>
          </div>

          <ul class="category-photo-block__list">
            <li v-if="itemInEdit.image">
              <img :src="itemInEdit?.image?.url" alt="" class="exit_img" />
              <Button
                class="category-photo-block__remove"
                :svg-icon="trashIcon"
                @click="removeExitImg"
                v-if="getRole() === 'admin'"
              />
            </li>
            <li v-for="(photo, indexPhoto) in photosList" :key="photo" v-else>
              <CardPhoto
                :img="photo.img"
                :file="photo"
                :isGenerate="true"
                @remove="handleRemove"
                :index="indexPhoto"
                is-hide-remove-btn
              />
              <Button
                class="category-photo-block__remove"
                :svg-icon="trashIcon"
                @click="handleRemove"
                v-if="getRole() === 'admin'"
              />
            </li>
          </ul>
        </div>
      </Form>

      <div class="custom-modal-footer">
        <Button @click="cancel">{{
          getMessageByLang("cancel", "Отмена")
        }}</Button>
        <Button :theme-color="'primary'" @click="updateCategory">{{
          getMessageByLang("btnSave", "Сохранить")
        }}</Button>
      </div>
    </div>
  </Dialog>

  <ModalDelete v-if="isShowModalDelete" @accept="modalDeleteAccept" @cancel="modalDeleteCancel" />
</template>

<script>
import {
  TreeList,
  mapTree,
  extendDataItem,
} from "@progress/kendo-vue-treelist";
import { Form } from "@progress/kendo-vue-form";
import { Button } from "@progress/kendo-vue-buttons";
import { Dialog } from "@progress/kendo-vue-dialogs";
import { cancelIcon, saveIcon } from "@progress/kendo-svg-icons";
import { dataString } from "../../composables/dataString";
import { pencilIcon, trashIcon } from "@progress/kendo-svg-icons";
import { getMessageByLang } from "@/utils/translate.js";
import { Upload } from "@progress/kendo-vue-upload";
import { getRole } from "@/utils/router";
import { isValidText } from "@/utils/form-validation";

import CardPhoto from "@/components/photos/CardPhoto";
import CategoryForm from "@/components/forms/CategoryForm.vue";
import VLoader from "@/ui-kit/components/VLoader.vue";
import ModalDelete from "@/components/modals/ModalDelete.vue";

import axios from "axios";
import { validPhotoExtentions } from "@/constants/File";
import {addListener, removeListener} from "@/utils/app";

export default {
  components: {
    TreeList,
    Button,
    Dialog,
    Form,
    CategoryForm,
    Upload,
    CardPhoto,
    VLoader,
    ModalDelete
  },
  data: function () {
    return {
      getMessageByLang: getMessageByLang,
      cancelIcon,
      saveIcon,
      filter: null,
      sort: null,
      pageable: true,
      gridData: [],
      dataItems: [],
      skip: 0,
      take: 10,
      total: 0,
      updatedData: [],
      editID: null,
      subItemsField: "children",
      expandField: "has_children",
      expanded: [],
      loader: true,
      itemInCreate: false,
      itemInEdit: false,
      pencilIcon,
      trashIcon,
      RefCategoryForm: null,
      isLoadingEdit: false,
      photosList: [],
      getRole: getRole,
      isValidText: isValidText,
      isShowModalDelete: false,
      deleteItem: null
    };
  },
  inject: {
    kendoLocalizationService: { default: null },
  },
  computed: {
    columns() {
      return [
        { field: "id", width: 60, title: "ID" },
        {
          field: "name",
          title: this.getMessageByLang("categoryName", "categoryName"),
          expandable: true,
        },
        { cell: "operation", filterable: false, width: "150px" },
      ];
    },

    processedData() {
      return this.addExpandField(this.dataItems);
    },
    dataState() {
      return {
        sort: this.sort,
        skip: this.skip,
        take: this.take,
      };
    },
  },
  created: function () {
    this.getData();
    addListener(this.addEscListener)
  },

  methods: {
    addEscListener(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.itemInCreate = undefined;
        this.itemInEdit = undefined;
        this.isShowModalDelete = false;
      }
    },

    showModalDelete(item) {
      this.deleteItem = item;
      this.isShowModalDelete = true;
    },

    modalDeleteAccept() {
      this.remove(this.deleteItem);
    },

    modalDeleteCancel() {
      this.isShowModalDelete = false;
    },

    removeExitImg() {
      this.itemInEdit.image = null;
    },

    onRemoveFile() {
      this.photosList = [];
    },

    handleRemove(options = null) {
      if (options) {
        this.photosList.splice(options.index, 1);
      }
    },

    onAddFile(file) {
      try {
        this.photosList = [];
        if (this.itemInEdit) {
          this.itemInEdit.image = null;
        } else {
          this.itemInCreate.image = null;
        }

        if (file?.affectedFiles) {
          const activeFile = file.affectedFiles[0];

          if (validPhotoExtentions.includes(activeFile.extension)) {
            this.photosList.push({
              ...activeFile,
              img: `${activeFile.uid}`,
              isNew: true,
            });

          } else {
            alert(this.getMessageByLang("selectAnotherFile", "Выберите другой формат файла"));
          }
        }
      } catch (err) {
        console.error(err);
      }
    },

    create() {
      this.photosList = [];
      this.itemInCreate = { parent_id: 0 };
    },
    async edit(dataItem) {
      this.photosList = [];
      this.isLoadingEdit = true;

      axios
        .get(`/api/v1/categories/` + dataItem.id)
        .then((response) => {
          this.itemInEdit = Object.assign({}, response.data.data);
        })
        .catch((err) => {
          console.error(err);
          this.itemInEdit = {};
        })
        .finally(() => {
          this.isLoadingEdit = false;
        });

      setTimeout(() => {
        this.isLoadingEdit = false;
      }, 400);
    },
    remove(dataItem) {
      axios
        .delete("/api/v1/categories/" + dataItem.id)
        .then((res) => {
          if (res.data) {
            if (res.data.message) {
              this.isShowModalDelete = false;
              this.getData();
            }
          }
        })
        .catch(function (error) {
          if (error.response) {
            console.error(error);
            alert(this.getMessageByLang("deleteError", "Ошибка удаления"));
          }
        });
    },
    cloneProduct(item) {
      return Object.assign({}, item);
    },
    cancel() {
      this.itemInEdit = undefined;
      this.itemInCreate = undefined;
    },
    onExpandChange(e) {
      this.expanded = e.value
        ? this.expanded.filter((id) => id !== e.dataItem.id)
        : [...this.expanded, e.dataItem.id];
    },
    addExpandField(dataTree) {
      return mapTree(dataTree, this.subItemsField, (item) =>
        extendDataItem(item, this.subItemsField, {
          [this.expandField]: this.expanded.includes(item.id),
        })
      );
    },
    getData: function () {
      const that = this;
      this.loader = true;
      this.dataState.take = 20;

      axios
        .get("/api/v1/categories/tree?" + dataString(this.dataState))
        .then((res) => {
          if (res.data) {
            if (res.data.message) {
              that.total = res.data.total;
              that.updatedData = [...res.data.data];
              that.gridData = res.data.data;
              that.dataItems = res.data.data;
            }
          }
        })
        .catch(function (error) {
          if (error.response) {
            if (error.response.data.message) {
              alert(error.response.data.message);
            }
          } else if (error.request) {
            console.error(error.request);
            alert(this.getMessageByLang("error", "Ошибка"));
          }
        })
        .finally(() => {
          this.loader = false;
        });
    },
    createCategory: function () {
      try {
        const invalidOptions = [];

        if (this.$refs.categoryFormCreate.form.errors) {
          if (
            this.$refs.categoryFormCreate.form.errors.name &&
            this.$refs.categoryFormCreate.form.errors.name.trim()
          ) {
            invalidOptions.push("name");
          }

          if (
            this.$refs.categoryFormCreate.form.errors.descr &&
            this.$refs.categoryFormCreate.form.errors.descr.trim()
          ) {
            invalidOptions.push("description");
          }
        }

        this.$refs.RefCategoryForm.showInvalidOptions(invalidOptions);
        this.$refs.categoryFormCreate.onSubmit();
      } catch (err) {
        console.error(err);
      }
    },
    savingPhoto(id) {
      const formData = new FormData();
      if (this.photosList.length > 0) {
        const item = this.photosList[0];
        const file = item.getRawFile();
        formData.append("image", file, file.name);
      }

      return axios.post(`/api/v1/categories/${id}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },

    async savePhoto(id, action = "create") {
      try {
        if (this.photosList.length > 0) {
          await this.savingPhoto(id);
        }

        if (action === "edit") {
          this.itemInEdit = undefined;
          this.isLoadingEdit = false;
        } else {
          this.itemInCreate = undefined;
        }

        this.getData();
      } catch (err) {
        console.error(err);
      }
    },

    isValidCategory(dataItem = {}) {
      const invalidOptions = [];

      if (!this.isValidText(dataItem.name)) {
        invalidOptions.push("name");
      }

      if (!this.isValidText(dataItem.descr)) {
        invalidOptions.push("description");
      }

      if (!dataItem.parent_id) {
        invalidOptions.push("category-parent");
      }

      if (invalidOptions.lenght) {
        this.$refs.RefCategoryForm.showInvalidOptions(invalidOptions);
      }

      return !invalidOptions.lenght;
    },

    handleCreateSubmit(dataItem) {
      dataItem.parent_id = this.$refs.RefCategoryForm.parent_id;
      const parent_id = this.$refs.RefCategoryForm.getParentId();
      dataItem.parent_id = parent_id;

      if (!this.isValidCategory(dataItem)) {
        return;
      }

      const formValues = JSON.stringify(dataItem, null, 2);
      var self = this;
      axios
        .post("/api/v1/categories/", formValues)
        .then(function (data) {
          self.savePhoto(data?.data?.data?.id || 0);
        })
        .catch(function (error) {
          if (error.response) {
            if (error.response.data.errors) {
              self.$refs.categoryFormCreate.form.errors.VALIDATION_SUMMARY =
                error.response.data.message;
              for (let key in error.response.data.errors) {
                self.$refs.categoryFormCreate.form.errors[key] =
                  error.response.data.errors[key][0];
              }
            } else {
              if (error.response.data.message) {
                self.$refs.categoryFormCreate.form.errors.VALIDATION_SUMMARY =
                  error.response.data.message;
              }
            }
          } else if (error.request) {
            console.error(error.request);
          }
        });
    },
    updateCategory: function () {
      try {
        const invalidOptions = [];

        if (this.$refs.categoryFormUpdate.form.errors) {
          if (
            this.$refs.categoryFormUpdate.form.errors.name &&
            this.$refs.categoryFormUpdate.form.errors.name.trim()
          ) {
            invalidOptions.push("name");
          }

          if (
            this.$refs.categoryFormUpdate.form.errors.descr &&
            this.$refs.categoryFormUpdate.form.errors.descr.trim()
          ) {
            invalidOptions.push("description");
          }
        }

        this.$refs.RefCategoryForm.showInvalidOptions(invalidOptions);
        this.$refs.categoryFormUpdate.onSubmit();
      } catch (err) {
        console.error(err);
      }
    },
    handleUpdateSubmit(dataItem) {
      const parent_id = this.$refs.RefCategoryForm.getParentId();
      dataItem.parent_id = parent_id;

      if (!this.isValidCategory(dataItem)) {
        return;
      }

      this.isLoadingEdit = true;

      const formValues = JSON.stringify(dataItem, null, 2);
      var self = this;
      axios
        .put("/api/v1/categories/" + dataItem.id, formValues)
        .then(function () {
          self.savePhoto(dataItem.id, "edit");
        })
        .catch(function (error) {
          self.isLoadingEdit = false;

          if (error.response) {
            if (error.response.data.errors) {
              self.$refs.categoryFormUpdate.form.errors.VALIDATION_SUMMARY =
                error.response.data.message;
              for (let key in error.response.data.errors) {
                self.$refs.categoryFormUpdate.form.errors[key] =
                  error.response.data.errors[key][0];
              }
            } else {
              if (error.response.data.message) {
                self.$refs.categoryFormUpdate.form.errors.VALIDATION_SUMMARY =
                  error.response.data.message;
              }
            }
          } else if (error.request) {
            console.error(error.request);
          }
        })
        .finally(() => {
          self.isLoadingEdit = false;
        });
    },
    createAppState: function (dataState) {
      this.take = dataState.take;
      this.skip = dataState.skip;
      this.filter = dataState.filter;
      this.sort = dataState.sort;
      this.getData();
    },
  },

  unmounted() {
    removeListener(this.addEscListener)
  }
};
</script>

<style lang="scss" scoped>
.category-photo-block {
  margin-top: 1rem;

  &__list {
    list-style: none;

    & li {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}

.exit_img {
  height: 128px;
  width: 128px;
  object-fit: cover;
}

.category-photo-block__upload {
  padding-left: 28px;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
</style>